import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Layers } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminResyncTiersButton — Round P108av+3 (Feb 2026).
 *
 * Companion to AdminRebuildStreaksButton in the "Streaks · on-demand"
 * admin group. Two different jobs that solve two different problems:
 *
 *   • Rebuild Streaks — scans `dividend_growth_cache` and recomputes
 *     each ticker's consecutive-year streak from raw annual dividend
 *     data. Detects cuts. Run this AFTER the Massive daily snapshot.
 *
 *   • Resync Tier Labels (this button) — force-reruns the sync from
 *     the curated `data/dividend_tier_lists.py` into
 *     `stock_cache.dividend_tier`, then re-warms the Heatmap
 *     collection. Run this WHENEVER the curated dict is edited (e.g.
 *     adding a new Aristocrat, removing a company that cut).
 *
 * Idempotent — safe to click any time. Only writes changes that
 * differ from the current cached value.
 */
export default function AdminResyncTiersButton() {
  const { user } = useAuth();
  const [firing, setFiring] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (user?.role !== 'admin') return null;

  const handleFire = async () => {
    if (firing) return;
    setFiring(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/tiers/resync`,
        {},
        { withCredentials: true, timeout: 120 * 1000 },
      );
      setLastResult(data);
      const c = data?.counts || {};
      toast.success('Tier resync complete', {
        description: `Kings ${c.king || 0} · Aristocrats ${c.aristocrat || 0} · Achievers ${c.achiever || 0}. Heatmap re-warm scheduled.`,
      });
    } catch (err) {
      toast.error('Tier resync failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setFiring(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleFire}
        disabled={firing}
        data-testid="admin-resync-tiers-fire"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: force-rerun the Kings/Aristocrats/Achievers sync from the curated data/dividend_tier_lists.py, then re-warm the Heatmap. Click this whenever the curated dict changes (add/remove a ticker, edit a streak). Idempotent, ~10 seconds."
      >
        {firing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
        Resync Tier Labels
      </button>
      {lastResult && (
        <span
          className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300"
          title={`Curated tier universe (what the Heatmap + TierChallenge pages display):\nKings ${lastResult.counts?.king}\nAristocrats ${lastResult.counts?.aristocrat}\nAchievers ${lastResult.counts?.achiever}\n\nUnderlying stock_cache.dividend_tier (single-highest-tier precedence — a King is only counted once, not re-counted as Aristocrat / Achiever even though it qualifies):\nKing ${lastResult.cache_counts?.king}\nAristocrat ${lastResult.cache_counts?.aristocrat}\nAchiever ${lastResult.cache_counts?.achiever}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          K {lastResult.counts?.king} · A {lastResult.counts?.aristocrat} · Ach {lastResult.counts?.achiever}
        </span>
      )}
    </div>
  );
}
